import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Interests as InterestIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";
import { getLeadInterested, createLeadInterested, updateLeadInterested, deleteLeadInterested } from '../../services/master';

const LeadInterested = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [leadInterestedData, setLeadInterestedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedLeadInterestedForDelete, setSelectedLeadInterestedForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingLeadInterested, setEditingLeadInterested] = useState(null);
  const [newLeadInterested, setNewLeadInterested] = useState({
    leadInterested: '',
    status: 'Active',
  });

  // Fetch lead interested
  const fetchLeadInterested = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const leadInterested = await getLeadInterested();
      setLeadInterestedData(leadInterested);
    } catch (err) {
      setApiError('Failed to load lead interested.');
      setLeadInterestedData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadInterested();
  }, []);

  // Filter logic
  const getFilteredLeadInterested = () => {
    return leadInterestedData.filter((li) => {
      const matchesSearch =
        li.leadInterested.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || li.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredLeadInterested = getFilteredLeadInterested();

  // Pagination
  const paginatedLeadInterested = filteredLeadInterested.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newLeadInterested.leadInterested.trim()) err.leadInterested = 'Lead Interested is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return leadInterestedData.some(
      (li) =>
        li.leadInterested.toLowerCase() === newLeadInterested.leadInterested.trim().toLowerCase() &&
        li.id !== editingLeadInterested?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingLeadInterested(null);
    setNewLeadInterested({
      leadInterested: '',
      status: 'Active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingLeadInterested(null);
    setNewLeadInterested({
      leadInterested: '',
      status: 'Active',
    });
    setErrors({});
  };

  const handleEdit = (leadInterested) => {
    setEditingLeadInterested(leadInterested);
    setNewLeadInterested({
      leadInterested: leadInterested.leadInterested,
      status: leadInterested.status,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (leadInterested) => {
    setSelectedLeadInterestedForDelete(leadInterested);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedLeadInterestedForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      await deleteLeadInterested(selectedLeadInterestedForDelete.id);
      setAlertConfig({ title: 'Success', message: 'Lead interested deleted successfully!' });
      setAlertOpen(true);
      fetchLeadInterested(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete lead interested.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedLeadInterestedForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ leadInterested: 'Lead interested already exists' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'projectType',
        details: {
          projectType: newLeadInterested.leadInterested.trim(),
        },
        status: newLeadInterested.status === 'Active',
      };

      if (editingLeadInterested) {
        await updateLeadInterested(editingLeadInterested.id, payload);
      } else {
        await createLeadInterested(payload);
      }

      setAlertConfig({
        title: 'Success',
        message: editingLeadInterested ? 'Lead interested updated successfully!' : 'Lead interested added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchLeadInterested(true);
    } catch (err) {
      setErrors({ leadInterested: 'Failed to save lead interested.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
    fetchLeadInterested(true);
  };

  const handleChange = (field, value) => {
    setNewLeadInterested((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InterestIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Lead Interested Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLeadInterested()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}
            >
              Add Lead Interested
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the lead interested <strong>{selectedLeadInterestedForDelete?.leadInterested}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchLeadInterested()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Lead Interested..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredLeadInterested.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} lead interested matching your criteria.` : 'No lead interested found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !leadInterestedData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Lead Interested</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLeadInterested.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No lead interested found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLeadInterested.map((leadInterested, index) => (
                    <TableRow key={leadInterested.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{leadInterested.leadInterested}</TableCell>
                      <TableCell>
                        <Chip
                          label={leadInterested.status}
                          color={leadInterested.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleEdit(leadInterested)} disabled={isLoading}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canWrite && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(leadInterested)} disabled={isLoading}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredLeadInterested.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLeadInterested ? 'Edit Lead Interested' : 'Add New Lead Interested'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Lead Interested"
                  value={newLeadInterested.leadInterested}
                  onChange={(e) => handleChange('leadInterested', e.target.value)}
                  error={!!errors.leadInterested}
                  helperText={errors.leadInterested}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newLeadInterested.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingLeadInterested ? 'Update' : 'Add'} Lead Interested
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default LeadInterested;

